import ClearIcon from '@mui/icons-material/Clear';
import { differenceBy } from 'lodash';
import type { Action } from 'material-table';
import type { OptionsObject, SnackbarKey, SnackbarMessage } from 'notistack';
import React from 'react';
import type { ResponseListLocationUsers, ResponseListMemberships, UserSummary } from '../../app/ApiGen';
import { errorSnackbar } from '../../components/ErrorSnackbar';
import type { SelectableItem } from '../../components/SelectionList';
import type { Normalised } from '../request';

interface RemoveUserProps {
  closeSnackbar: () => void;
  enqueueSnackbar: (message: SnackbarMessage, options?: OptionsObject) => SnackbarKey;
  refetch: () => Promise<ResponseListLocationUsers | ResponseListMemberships | null>;
  removeUser: (users: Normalised<UserSummary>) => Promise<void>;
  source: string;
}

export const getSelectableUsers = (allUsers: UserSummary[], excludedUsers: UserSummary[]): SelectableItem[] => {
  const userOptions = differenceBy(allUsers, excludedUsers, 'id');
  return userOptions.map((u) => ({
    id: u.id,
    title: u.attributes.name ?? u.attributes.email,
    subtitle: u.attributes.email,
    color: u.attributes.color,
    image: u.attributes.avatar,
  }));
};

export function getDeleteUserAction({
  closeSnackbar,
  enqueueSnackbar,
  refetch,
  removeUser,
  source,
}: RemoveUserProps): Action<Normalised<UserSummary>> {
  return {
    icon: () => <ClearIcon />,
    tooltip: 'Remove user',
    onClick: (_: unknown, user) => {
      if (Array.isArray(user)) {
        throw new Error('Removing multiple users is not supported');
      }
      removeUser(user)
        .then(async () => {
          await refetch();
          enqueueSnackbar(`User ${user.email} was successfully removed from ${source}`, { variant: 'success' });
        })
        .catch((e) => errorSnackbar('Unable to remove user', closeSnackbar, enqueueSnackbar, e));
    },
  };
}
